import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Btn } from "./Kit";

const TIPO = { aprobar: "bg-emerald-500", devolver: "bg-orange-500", rechazar: "bg-rose-500", enviado: "bg-[#E8643C]" };

export const useNotifications = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => http.get("/notifications").then((r) => setItems(r.data)).catch(() => null), []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  return [items, load];
};

export const NotifBell = ({ base = "/deals" }) => {
  const [items, load] = useNotifications();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const unread = items.filter((n) => !n.read).length;
  const go = async (n) => {
    await http.post("/notifications/read", { expediente_id: n.expediente_id });
    setOpen(false); load(); nav(n.expediente_id ? `${base}/${n.expediente_id}` : base === "/deals" ? "/" : "/portal/comisiones");
  };
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid="notif-bell" title="Notificaciones"
        className="relative rounded-xl border border-[#E2DDD5] bg-white p-2.5 text-[#5C5850] transition-colors hover:text-[#E8643C]">
        <Bell size={16} />
        {unread > 0 && <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-[#E8643C] px-1 text-[10px] font-bold text-white" data-testid="notif-count">{unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 w-96 overflow-hidden rounded-2xl border border-[#E2DDD5] bg-white shadow-xl" data-testid="notif-panel">
          <div className="flex items-center justify-between border-b border-[#F2F0EC] px-5 py-3">
            <p className="font-semibold">Notificaciones</p>
            <button className="text-xs font-semibold text-[#E8643C] hover:underline" data-testid="notif-read-all"
              onClick={async () => { await http.post("/notifications/read", {}); load(); }}>Marcar todo como leído</button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && <p className="px-5 py-8 text-center text-sm text-[#8C867A]">Sin notificaciones.</p>}
            {items.map((n) => (
              <button key={n.id} onClick={() => go(n)} data-testid={`notif-item-${n.id}`}
                className={`flex w-full gap-3 px-5 py-3 text-left transition-colors hover:bg-[#FAF8F5] ${n.read ? "opacity-60" : ""}`}>
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${TIPO[n.tipo] || "bg-[#C9C3B8]"}`} />
                <span className="min-w-0"><span className="block truncate text-sm font-semibold">{n.expediente}</span>
                  <span className="block text-sm text-[#5C5850]">{n.texto}</span>
                  <span className="block text-xs text-[#8C867A]">{new Date(n.created_at).toLocaleString("es-MX")}</span></span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ProfileModal = ({ onClose }) => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user.name, puesto: user.puesto || "" });
  const [tipos, setTipos] = useState([]);
  const [prefs, setPrefs] = useState(user.email_prefs || {});
  const [notifEmail, setNotifEmail] = useState(user.notif_email || "");
  useEffect(() => { http.get("/auth/email-types").then((r) => setTipos(r.data)); }, []);
  const save = async () => {
    try { const r = await http.patch("/auth/me", { ...f, email_prefs: prefs, notif_email: notifEmail }); setUser(r.data); toast.success("Perfil actualizado"); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = "mt-1.5 w-full rounded-xl border border-[#E2DDD5] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#E8643C] focus:bg-white";
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-[#111]/40 p-4 backdrop-blur-sm" data-testid="profile-modal">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl">
        <div className="flex items-start justify-between"><h3 className="font-serif-display text-2xl font-bold">Mi perfil</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-[#8C867A] hover:bg-[#F2F0EC]" data-testid="profile-close"><X size={18} /></button></div>
        <p className="mt-1 text-sm text-[#5C5850]">Tu nombre y puesto aparecen en las firmas del Credit Memo.</p>
        {[["name", "Nombre completo"], ["puesto", "Puesto"]].map(([k, l]) => (
          <label key={k} className="mt-5 block text-xs font-semibold uppercase tracking-widest text-[#8C867A]">{l}
            <input data-testid={`profile-${k}`} className={inp} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>
        ))}
        <p className="mt-6 text-xs font-semibold uppercase tracking-widest text-[#8C867A]">Avisos por correo</p>
        <input data-testid="profile-notif-email" type="email" className={inp} value={notifEmail} placeholder={`${user.email} (correo de acceso)`}
          onChange={(e) => setNotifEmail(e.target.value)} />
        <p className="mt-1 text-xs text-[#8C867A]">Opcional: buzón donde recibir los avisos. Vacío = tu correo de acceso.</p>
        <div className="mt-2 space-y-1" data-testid="profile-email-prefs">
          {tipos.map((t) => (
            <label key={t.tipo} className="flex items-center justify-between rounded-xl px-3 py-2 text-sm hover:bg-[#FAF8F5]">
              <span>{t.label}{t.fase > 2 && <span className="ml-2 rounded-full bg-[#F2F0EC] px-2 py-0.5 text-[10px] font-semibold text-[#8C867A]">Fase {t.fase}</span>}</span>
              <input type="checkbox" className="h-4 w-4 accent-[#E8643C]" data-testid={`email-pref-${t.tipo}`} checked={prefs[t.tipo] !== false}
                onChange={(e) => setPrefs({ ...prefs, [t.tipo]: e.target.checked })} />
            </label>
          ))}
        </div>
        <div className="mt-8 flex justify-end gap-3"><Btn kind="secondary" onClick={onClose}>Cancelar</Btn>
          <Btn kind="accent" disabled={f.name.trim().length < 2} onClick={save} data-testid="profile-save">Guardar</Btn></div>
      </div>
    </div>
  );
};
